import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useApi } from '@hooks/useApi';

interface PasswordStrength {
  score: number;
  level: 'very_weak' | 'weak' | 'fair' | 'good' | 'strong';
  feedback: string[];
  requirements_met: string[];
  requirements_failed: string[];
}

interface PasswordStrengthResponse {
  is_valid: boolean;
  strength: PasswordStrength;
}

interface PasswordStrengthMeterProps {
  password: string;
  className?: string;
}

const StrengthMeter = styled.div`
  margin-top: 0.5rem;
`;

const ProgressBar = styled.div<{ score: number; level: string }>`
  width: 100%;
  height: 6px;
  background: var(--neutral-200);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 0.5rem;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${(props) => props.score}%;
    background: ${(props) => {
      switch (props.level) {
        case 'very_weak':
        case 'weak':
          return 'var(--danger-500)';
        case 'fair':
          return 'var(--warning-500)';
        case 'good':
          return 'var(--success-500)';
        case 'strong':
          return 'var(--success-600)';
        default:
          return 'var(--neutral-400)';
      }
    }};
    transition: all 0.3s ease;
  }
`;

const StrengthText = styled.div<{ level: string }>`
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: ${(props) => {
    switch (props.level) {
      case 'very_weak':
      case 'weak':
        return 'var(--danger-600)';
      case 'fair':
        return 'var(--warning-600)';
      case 'good':
      case 'strong':
        return 'var(--success-600)';
      default:
        return 'var(--text-muted)';
    }
  }};
`;

const FeedbackList = styled.ul`
  margin: 0;
  padding-left: 1rem;
  font-size: 0.8rem;
  line-height: 1.3;
  color: var(--text-muted);
`;

const FeedbackItem = styled.li<{ type: 'positive' | 'negative' }>`
  color: ${(props) =>
    props.type === 'positive' ? 'var(--success-600)' : 'var(--danger-600)'};

  &::marker {
    color: ${(props) =>
      props.type === 'positive' ? 'var(--success-600)' : 'var(--danger-600)'};
  }
`;

const getStrengthText = (level: string) => {
  switch (level) {
    case 'very_weak':
      return 'Muy débil';
    case 'weak':
      return 'Débil';
    case 'fair':
      return 'Regular';
    case 'good':
      return 'Buena';
    case 'strong':
      return 'Fuerte';
    default:
      return 'Evaluando...';
  }
};

export const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({
  password,
  className,
}) => {
  const { apiCall } = useApi();
  const [strength, setStrength] = useState<PasswordStrength | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!password) {
      setStrength(null);
      return;
    }

    const checkStrength = async () => {
      setLoading(true);
      try {
        const response: PasswordStrengthResponse = await apiCall(
          '/password/strength',
          {
            method: 'POST',
            body: { password },
          },
        );

        if (response?.strength) {
          setStrength(response.strength);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error checking password strength:', error);
      } finally {
        setLoading(false);
      }
    };

    // Debounce the API call
    const timeoutId = setTimeout(checkStrength, 300);
    return () => clearTimeout(timeoutId);
  }, [password, apiCall]);

  if (!password) {
    return null;
  }

  if (loading) {
    return (
      <StrengthMeter className={className}>
        <ProgressBar score={0} level="weak">
          <div
            style={{
              background: 'var(--neutral-300)',
              width: '100%',
              height: '100%',
            }}
          />
        </ProgressBar>
        <StrengthText level="weak">Evaluando...</StrengthText>
      </StrengthMeter>
    );
  }

  if (!strength) {
    return null;
  }

  return (
    <StrengthMeter className={className}>
      <ProgressBar score={strength.score} level={strength.level} />
      <StrengthText level={strength.level}>
        Fortaleza: {getStrengthText(strength.level)} ({strength.score}/100)
      </StrengthText>

      {strength.feedback.length > 0 && (
        <FeedbackList>
          {strength.feedback.map((feedback, index) => (
            <FeedbackItem
              key={index}
              type={
                strength.requirements_failed.length > 0
                  ? 'negative'
                  : 'positive'
              }
            >
              {feedback}
            </FeedbackItem>
          ))}
        </FeedbackList>
      )}
    </StrengthMeter>
  );
};
